import React, { useEffect, useMemo, useState } from "react";
import { Controller, useForm } from "react-hook-form";
import { useDispatch, useSelector } from "react-redux";

import { AppDispatch, RootState } from "../../redux/store";
import {
  setFinalData,
  setSelectedAccountName,
  setSelectedOrganization,
  setselectedDistribution,
} from "../../redux/reducers/OTISReducer";
import axios from "axios";
import { useParams } from "react-router-dom";
import { API } from "../../constants/API";
import { OptionType } from "../../common-components/TypesofInterfaces";
import { NO_ACCOUNT_ATTACHED, NO_ORGANIZATION_ATTACHED } from "./TableBody/FilterOTISData";
import ControlledSelect from "./ControlledSearchSelect";
import { setExistedRow, setSelectedAccountId, setSelectedOrgId, setSelectedRepId } from "../../redux/reducers/Validationslice";
import jsPDF from "jspdf";
import {
  ISBNData,
} from "../../redux/reducers/ISBNSReducer";
import { user } from "../../redux/reducers/UserReducer";
import { toast } from "react-toastify";

interface DataProp {
  data: any[];
  setLoadingTest: (loading: boolean) => void;
  handleRemoveRow?: () => any; // Allow thunk or plain action
  setFormData: React.Dispatch<React.SetStateAction<{ [key: string]: any }>>;
  onRefetch?: () => void;
  // Action buttons (when provided, rendered next to Export)
  userRole?: string[];
  addRow?: boolean;
  handleAddRow?: () => void;
  handleRemoveRowAction?: (id: string) => void;
  handleSaveAll?: () => void;
  isSaving?: boolean;
  setModal?: (val: boolean) => void;
  isLoading?: boolean;
}

const SearchControls: React.FC<DataProp> = ({
  data,
  setLoadingTest,
  handleRemoveRow,
  setFormData,
  onRefetch,
  userRole,
  addRow,
  handleAddRow,
  handleRemoveRowAction,
  handleSaveAll,
  isSaving,
  setModal,
  isLoading: isLoadingProp,
}) => {
  const fileInputRef = React.useRef<HTMLInputElement>(null);
  const [uploading, setUploading] = useState(false);
  const [distributionOptions, setDistributionOptions] = useState<OptionType[]>([]);
  const [accountNameOptions, setAccountNameOptions] = useState<OptionType[]>([]);
  const [orgOption, setOrgOption] = useState<OptionType[]>([]);
  const [filteredData, setFilteredData] = useState<any[]>([]); // State for filtered data
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [saveFlag, setSaveFlag] = useState<string | null>(localStorage.getItem("saveFlag")); // Initialize saveFlag state
  const params = useParams();

  const segId = params.segId ? params.segId : localStorage.getItem("segId");

  const [isbn_title_options, set_ISBN_TITLE_OPTION] = useState<OptionType[]>(
    []
  );
  const [isbn_number_options, set_ISBN_NUMBER_OPTION] = useState<OptionType[]>(
    []
  );
  const [exportInProgress, setExportInProgress] = useState(false);
  const [meetingReportInProgress, setMeetingReportInProgress] = useState(false);
  const { control, handleSubmit, watch, setValue } = useForm();

  const dispatch = useDispatch<AppDispatch>();
  const selectedDistribution = useSelector(
    (state: RootState) => state.OTISReducer.selectedDistribution
  );
  const selectedOrganization = useSelector(
    (state: RootState) => state.OTISReducer.selectedOrganization
  );
  const selectedAccountName = useSelector(
    (state: RootState) => state.OTISReducer.selectedAccountName
  );
  const segDetails = useSelector((state: RootState) => state.OTISReducer.data.segDetails);
  const segTitleNotes = useSelector(
    (state: RootState) => state.OTISReducer.data.SEGTitle
  );
  const { selectedAccountID, selectedOrgID, selectedRepID, existedRow } = useSelector(
    (state: any) => state.selection
  );
  const isbns = useSelector(ISBNData);
  const oldISBNS = isbns?.length ? isbns.map((isbn) => isbn._id) : [];
  const selectedISBNS = useSelector(
    (state: RootState) => state.isbnReducer.selectedISBNS ?? []
  );
  const selectedTitles = useSelector(
    (state: RootState) => state.isbnReducer.selectedTitles ?? []
  );
  // ISBN list from fetchIsbns_all (all summary filters) — used for export
  const Allisbns = useSelector(
    (state: RootState) => state.isbnReducer.Allisbns ?? []
  );
  const isbnLimit = useSelector(
    (state: RootState) => state.isbnReducer.isbnLimit
  );
  const isbnSkip = useSelector(
    (state: RootState) => state.isbnReducer.isbnSkip
  );
  const userData = useSelector(user);
  const isViewer = userData?.role?.includes("viewer");

  // Normalize values to string | null
  const normalizeValue = (value: string | boolean | null): string | null =>
    typeof value === "string" ? value : null;
  const noSegId = useSelector((state: RootState) => state.OTISReducer.noSegId);

  useEffect(() => {
    setValue(
      "AboveRepCode",
      selectedDistribution ? { value: selectedDistribution, label: selectedDistribution } : null
    );
    setValue(
      "AboveOrganization",
      selectedOrganization ? { value: selectedOrganization, label: selectedOrganization } : null
    );
    setValue(
      "AboveAccountName",
      selectedAccountName ? { value: selectedAccountName, label: selectedAccountName } : null
    );
  }, [selectedDistribution, selectedOrganization, selectedAccountName, setValue]);

  const restoreFilterState = () => {
    const savedFilterState = JSON.parse(localStorage.getItem("savedFilterState") || "{}");
    const { selectedRepIDS, selectedOrgIDs, selectedAccountIDs } = savedFilterState;


    if (selectedRepIDS?.value) {
      setValue("AboveRepCode", selectedRepIDS);
      dispatch(setselectedDistribution(selectedRepIDS.value || ""));
    }
    if (selectedOrgIDs?.value) {
      setValue("AboveOrganization", selectedOrgIDs);
      dispatch(setSelectedOrganization(selectedOrgIDs.value || ""));
    }
    if (selectedAccountIDs?.value) {
      setValue("AboveAccountName", selectedAccountIDs);
      dispatch(setSelectedAccountName(selectedAccountIDs.value || ""));
    }
  };

  // Helpers for "no org/account attached" filters
  const hasNoOrganization = (record: any) =>
    !record.organizations?.length ||
    record.organizations.every((org: any) => !(org?.orgName?.trim?.() || org?.orgName));
  const hasNoAccounts = (org: any) =>
    !org?.accounts?.length ||
    org.accounts.every((acc: any) => !(acc?.accountName?.trim?.() || acc?.accountName));
  const recordHasNoAccounts = (record: any) =>
    !record.organizations?.length ||
    record.organizations.every((org: any) => hasNoAccounts(org));

  // Client-side filtering function
  const fetchFilteredData = async (filters: {
    segId: string | null;
    distribution?: string | null;
    organization?: string | null;
    account?: string | null;
  }) => {
    setIsLoading(true);
    try {
      let filteredData = data;
      if (filters.distribution) {
        filteredData = filteredData.filter(
          (record) => record.DISTRIBUTION_CHANNEL_NAME === filters.distribution
        );
      }
      if (filters.organization) {
        filteredData = filteredData.filter((record) =>
          record.organizations.some((org: any) => (org.orgName?.trim?.() || org.orgName) === filters.organization)
        );
      }
      if (filters.account) {
        filteredData = filteredData.filter((record) =>
          record.organizations.some((org: any) =>
            org.accounts.some((account: any) => (account.accountName?.trim?.() || account.accountName) === filters.account)
          )
        );
      }
      return filteredData;
    } catch (err) {
      console.error("Error filtering data:", err);
      alert("Failed to filter data. Please try again.");
      return [];
    } finally {
      setIsLoading(false);
    }
  };

  // Export uses ISBN list from fetchIsbns_all (all summary filters). Runs in background; user can keep using the app.
  const downloadSegDetailsData = (data: any) => {
    const exportIsbnsFromAllFilters =
      Allisbns?.length > 0
        ? [...new Set(Allisbns.map((isbn: any) => isbn.ISBN ?? isbn._id).filter(Boolean))]
        : [...new Set([...selectedISBNS, ...selectedTitles])];

    const payload: Record<string, unknown> = {
      org: selectedOrganization || "",
      DISTRIBUTION_CHANNEL_NAME: selectedDistribution || "",
      account: selectedAccountName || "",
    };
    if (exportIsbnsFromAllFilters.length > 0) {
      payload.isbns = exportIsbnsFromAllFilters;
    } else {
      payload.segId = segId;
    }

    setExportInProgress(true);
    const toastId = "seg-detail-export";
    toast.loading("Export started. You can continue using the app. We'll notify you when it's ready.", {
      toastId,
      autoClose: false,
    });

    axios
      .post(API.Download_fetchOTISRecords, payload, { responseType: "blob" })
      .then((response) => {
        const contentDisposition = response.headers["content-disposition"];
        const fileName =
          contentDisposition?.match(/filename="(.+)"/)?.[1] ||
          `SEG Account Details - ${segDetails?.EAN ?? "Export"}.xlsx`;
        const contentType = response.headers["content-type"];
        const url = window.URL.createObjectURL(
          new Blob([response.data], {
            type:
              contentType ||
              "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
          })
        );
        const a = document.createElement("a");
        a.href = url;
        a.download = fileName;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        window.URL.revokeObjectURL(url);

        const summaryHeader =
          response.headers["x-export-summary"] ||
          response.headers["X-Export-Summary"];
        let message = "Export complete.";
        let toastType: "success" | "warning" = "success";
        if (summaryHeader) {
          try {
            const summary = JSON.parse(summaryHeader);
            if (summary.message) {
              message = summary.message;
              if (
                (summary.missingIsbns && summary.missingIsbns.length > 0) ||
                (summary.segsWithNoData && summary.segsWithNoData.length > 0)
              ) {
                toastType = "warning";
              }
            }
          } catch {
            // ignore
          }
        }
        toast.update(toastId, {
          render: message,
          type: toastType,
          isLoading: false,
          autoClose: 5000,
        });
      })
      .catch(async (err: any) => {
        console.error("Error downloading file:", err);
        let errMsg = "Failed to download the file. Please try again.";
        const errData = err?.response?.data;
        if (errData instanceof Blob) {
          try {
            const text = await errData.text();
            const parsed = JSON.parse(text);
            errMsg = parsed?.message || parsed?.exportSummary?.message || errMsg;
          } catch {
            // keep default errMsg
          }
        } else if (errData && typeof errData === "object") {
          errMsg = errData?.message || errData?.exportSummary?.message || errMsg;
        }
        toast.update(toastId, {
          render: errMsg,
          type: "error",
          isLoading: false,
          autoClose: 5000,
        });
      })
      .finally(() => {
        setExportInProgress(false);
      });
  };




  useEffect(() => {
    console.log("settingid", segDetails?._id);
    if (isbns?.length) {
      set_ISBN_NUMBER_OPTION(
        isbns.map((isbn: any) => ({
          value: isbn._id ?? "",
          label: isbn.ISBN ?? "",
        }))
      );
      set_ISBN_TITLE_OPTION(
        isbns.map((isbn: any) => ({
          value: isbn._id ?? "",
          label: isbn.FULL_TITLE ?? "",
        }))
      );
    }
  }, [isbns]);

  // Cleanup localStorage on component unmount
  useEffect(() => {
    return () => {
      // Remove filter-related localStorage keys when the component unmounts
      localStorage.removeItem("selectedDistribution");
      localStorage.removeItem("selectedAboveOrganization");
      localStorage.removeItem("selectedAboveAccountName");
      localStorage.removeItem("savedFilterState");
      localStorage.removeItem("saveFlag");
    };
  }, []);

  // Initialize dropdown options; show "No Organization/Account Attached" as non-selectable label when no options
  useEffect(() => {
    if (data?.length) {
      const uniqueRepCodes = Array.from(
        new Set(data.map((record) => record.DISTRIBUTION_CHANNEL_NAME).filter(Boolean))
      ).map((distribution) => ({ value: distribution, label: distribution }));

      const orgNames = data.flatMap((record) =>
        (record.organizations || []).map((org: any) => org?.orgName?.trim?.() || org?.orgName || "")
      );
      const hasRecordsWithNoOrg = data.some(
        (record) => !record.organizations?.length || record.organizations.every((org: any) => !(org?.orgName?.trim?.() || org?.orgName))
      );
      const uniqueOrganizations = Array.from(new Set(orgNames))
        .filter((name) => name !== "")
        .map((orgName) => ({ value: orgName, label: orgName }));
      const orgOptionFinal =
        uniqueOrganizations.length === 0 && hasRecordsWithNoOrg
          ? [{ value: "", label: NO_ORGANIZATION_ATTACHED, isDisabled: true } as OptionType & { isDisabled?: boolean }]
          : uniqueOrganizations;

      const accountNames = data.flatMap((record) =>
        (record.organizations || []).flatMap((org: any) =>
          (org.accounts || []).map((acc: any) => acc?.accountName?.trim?.() || acc?.accountName || "")
        )
      );
      const hasRecordsWithNoAccount = data.some((record) =>
        (record.organizations || []).some((org: any) => !org?.accounts?.length || org.accounts.every((a: any) => !(a?.accountName?.trim?.() || a?.accountName)))
      );
      const uniqueAccountNames = Array.from(new Set(accountNames))
        .filter((name) => name !== "")
        .map((accountName) => ({ value: accountName, label: accountName }));
      const accountOptionFinal =
        uniqueAccountNames.length === 0 && hasRecordsWithNoAccount
          ? [{ value: "", label: NO_ACCOUNT_ATTACHED, isDisabled: true } as OptionType & { isDisabled?: boolean }]
          : uniqueAccountNames;

      setDistributionOptions(uniqueRepCodes);
      setOrgOption(orgOptionFinal);
      setAccountNameOptions(accountOptionFinal);
    }
  }, [data]);

  // Update dropdown options based on selections
  useEffect(() => {
    let filteredDistributions: OptionType[] = [];
    let filteredOrganizations: OptionType[] = [];
    let filteredAccounts: OptionType[] = [];

    // Start with all data
    let filteredData = data;

    // Apply distribution filter
    if (selectedDistribution) {
      filteredData = filteredData.filter(
        (record) => record.DISTRIBUTION_CHANNEL_NAME === selectedDistribution
      );
    }

    // Apply account filter (if account is selected, limit organizations and distributions)
    if (selectedAccountName) {
      filteredData = filteredData.filter((record) =>
        record.organizations.some((org: any) =>
          org.accounts.some((account: any) => (account.accountName?.trim?.() || account.accountName) === selectedAccountName)
        )
      );
      filteredOrganizations = filteredData
        .flatMap((record) =>
          record.organizations.filter((org: any) =>
            org.accounts.some((account: any) => (account.accountName?.trim?.() || account.accountName) === selectedAccountName)
          )
        )
        .map((org: any) => ({ value: org?.orgName?.trim?.() || org?.orgName, label: org?.orgName?.trim?.() || org?.orgName }));
      filteredAccounts = [{ value: selectedAccountName, label: selectedAccountName }];
    }
    // Apply organization filter (if organization is selected, limit accounts)
    else if (selectedOrganization) {
      filteredData = filteredData.filter((record) =>
        record.organizations.some((org: any) => (org.orgName?.trim?.() || org.orgName) === selectedOrganization)
      );
      filteredOrganizations = [{ value: selectedOrganization, label: selectedOrganization }];
      filteredAccounts = filteredData
        .flatMap((record) =>
          record.organizations
            .filter((org: any) => (org.orgName?.trim?.() || org.orgName) === selectedOrganization)
            .flatMap((org: any) =>
              (org.accounts || []).map((account: any) => ({
                value: account?.accountName?.trim?.() || account?.accountName,
                label: account?.accountName?.trim?.() || account?.accountName,
              }))
            )
        );
    }
    // No account or organization selected, show all options
    else {
      filteredOrganizations = filteredData.flatMap((record) =>
        (record.organizations || []).map((org: any) => ({ value: org?.orgName?.trim?.() || org?.orgName, label: org?.orgName?.trim?.() || org?.orgName }))
      );
      filteredAccounts = filteredData.flatMap((record) =>
        (record.organizations || []).flatMap((org: any) =>
          (org.accounts || []).map((account: any) => ({
            value: account?.accountName?.trim?.() || account?.accountName,
            label: account?.accountName?.trim?.() || account?.accountName,
          }))
        )
      );
    }

    // Update distribution options based on filtered data
    filteredDistributions = Array.from(
      new Set(filteredData.map((record) => record.DISTRIBUTION_CHANNEL_NAME).filter(Boolean))
    ).map((distribution) => ({ value: distribution, label: distribution }));

    // Remove duplicates; filter out empty labels; when no options but data has no-org/no-account, show disabled label only
    filteredOrganizations = Array.from(new Set(filteredOrganizations.map((o) => o.value).filter(Boolean))).map(
      (value) => filteredOrganizations.find((o) => o.value === value)!
    );
    filteredAccounts = Array.from(new Set(filteredAccounts.map((a) => a.value).filter(Boolean))).map(
      (value) => filteredAccounts.find((a) => a.value === value)!
    );
    const hasRecordsWithNoOrg = filteredData.some((r) => hasNoOrganization(r));
    const hasRecordsWithNoAccount = filteredData.some((r) => recordHasNoAccounts(r));
    if (filteredOrganizations.length === 0 && hasRecordsWithNoOrg) {
      filteredOrganizations = [{ value: "", label: NO_ORGANIZATION_ATTACHED, isDisabled: true } as OptionType & { isDisabled?: boolean }];
    }
    if (filteredAccounts.length === 0 && hasRecordsWithNoAccount) {
      filteredAccounts = [{ value: "", label: NO_ACCOUNT_ATTACHED, isDisabled: true } as OptionType & { isDisabled?: boolean }];
    }

    setDistributionOptions(filteredDistributions);
    setOrgOption(filteredOrganizations);
    setAccountNameOptions(filteredAccounts);
  }, [selectedDistribution, selectedOrganization, selectedAccountName, data]);

  // Restore filter state on mount and when existedRow/saveFlag changes
  useEffect(() => {
    restoreFilterState();
  }, []);

  // Handle existed row and saveFlag for dropdown population
  useEffect(() => {
    const saveFlag = localStorage.getItem("saveFlag");
    if (saveFlag === "true" || existedRow) {
      // Ensure valid OptionType objects are set
      if (selectedRepID?.value) {
        setValue("AboveRepCode", { value: selectedRepID.value, label: selectedRepID.label });
      }
      if (selectedOrgID?.value) {
        setValue("AboveOrganization", { value: selectedOrgID.value, label: selectedOrgID.label });
      }
      if (selectedAccountID?.value) {
        setValue("AboveAccountName", { value: selectedAccountID.value, label: selectedAccountID.label });
      }
    }
  }, [selectedRepID, selectedOrgID, selectedAccountID, existedRow, setValue]);


  
  // Handle filter changes
  const handleFilterChange = async (field: string, selected: OptionType | null) => {
    setIsLoading(true);
    setFormData({})
    dispatch(setFinalData(null));
    dispatch(setFinalData([]));


    const value = selected?.value || null;

    // Update Redux state without clearing valid selections
    if (field === "AboveRepCode") {
      dispatch(setselectedDistribution(value));
      // Only clear dependent fields if the distribution changes and they are invalid
      if (value !== selectedDistribution && value !== null) {
        const isOrgValid = selectedOrganization && data.some((record) =>
          record.DISTRIBUTION_CHANNEL_NAME === value &&
          record.organizations.some((org: any) => (org.orgName?.trim?.() || org.orgName) === selectedOrganization)
        );
        const isAccountValid = selectedAccountName && data.some((record) =>
          record.DISTRIBUTION_CHANNEL_NAME === value &&
          record.organizations.some((org: any) =>
            org.accounts.some((account: any) => (account.accountName?.trim?.() || account.accountName) === selectedAccountName)
          )
        );

        if (!isOrgValid) {
          setValue("AboveOrganization", null);
          dispatch(setSelectedOrganization(null));
          dispatch(setSelectedOrgId(null));
        }
        if (!isAccountValid) {
          setValue("AboveAccountName", null);
          dispatch(setSelectedAccountName(null));
          dispatch(setSelectedAccountId(null));
        }
      }
    } else if (field === "AboveOrganization") {
      dispatch(setSelectedOrganization(value));
      // Only clear account if it’s invalid for the selected organization and a non-null organization is selected
      if (value !== selectedOrganization && value !== null) {
        const isAccountValid = selectedAccountName && data.some((record) =>
          record.organizations.some((org: any) =>
            (org.orgName?.trim?.() || org.orgName) === value &&
            org.accounts.some((account: any) => (account.accountName?.trim?.() || account.accountName) === selectedAccountName)
          )
        );
        if (!isAccountValid) {
          setValue("AboveAccountName", null);
          dispatch(setSelectedAccountName(null));
        }
      }
    } else if (field === "AboveAccountName") {
      dispatch(setSelectedAccountName(value));
      // Only clear organization if it’s invalid for the selected account
      if (value !== selectedAccountName && value !== null) {
        const isOrgValid = selectedOrganization && data.some((record) =>
          record.organizations.some((org: any) =>
            (org.orgName?.trim?.() || org.orgName) === selectedOrganization &&
            org.accounts.some((account: any) => (account.accountName?.trim?.() || account.accountName) === value)
          )
        );
        if (!isOrgValid) {
          setValue("AboveOrganization", null);
          dispatch(setSelectedOrganization(null));
          dispatch(setSelectedOrgId(null));
        }
      }
    }

    // Handle row removal if provided
    if (handleRemoveRow) {
      try {
        dispatch(handleRemoveRow());
      } catch (error) {
        console.error("Error dispatching handleRemoveRow:", error);
      }
    }

    // Fetch filtered data
    await fetchFilteredData({
      segId,
      distribution: normalizeValue(
        field === "AboveRepCode" ? value : selectedDistribution
      ),
      organization: normalizeValue(
        field === "AboveOrganization" ? value : selectedOrganization
      ),
      account: normalizeValue(
        field === "AboveAccountName" ? value : selectedAccountName
      ),
    });

    setIsLoading(false);
  };

  // Clear all selections
  const handleClearSearchBoxes = () => {
    setValue("AboveRepCode", null);
    setValue("AboveOrganization", null);
    setValue("AboveAccountName", null);
    dispatch(setselectedDistribution(null));
    dispatch(setSelectedOrganization(null));
    dispatch(setSelectedAccountName(null));
    dispatch(setSelectedRepId(null));
    dispatch(setSelectedOrgId(null));
    dispatch(setSelectedAccountId(null));
    localStorage.removeItem("selectedDistribution");
    localStorage.removeItem("selectedAboveOrganization");
    localStorage.removeItem("selectedAboveAccountName");
    localStorage.removeItem("savedFilterState");
    localStorage.removeItem("saveFlag");
  };


  // Clear filters only when leaving the detail page (noSegId), not when segId is set, so persisted filters survive ISBN navigation.
  useEffect(() => {
    if (noSegId) {
      handleClearSearchBoxes();
    }
  }, [noSegId]);
  const aboveRepCode = watch("AboveRepCode");
  const aboveOrganization = watch("AboveOrganization");
  const aboveAccountName = watch("AboveAccountName");
  const isClearDisabled =
    !aboveRepCode && !aboveOrganization && !aboveAccountName && !selectedDistribution && !selectedAccountName && !selectedOrganization;

  const isRowEmpty = (row: any) => {
    const skipKeys = ["isNew", "_id", "createdAt", "updatedAt"]; // Add any other metadata keys to skip
    return Object.entries(row).every(([key, value]) => {
      if (skipKeys.includes(key)) return true;
      if (Array.isArray(value)) return value.length === 0;
      return value === null || value === "" || value === undefined || value === 0;
    });
  };

  const isExportDisabled = useMemo(() => {
    // If export is already in progress, disable to avoid duplicate requests
    if (exportInProgress) return true;
    // Allow export when we have ISBN list from all summary filters (Allisbns)
    if (Allisbns?.length > 0) return false;
    // If no Allisbns: require current view data (single-seg export)
    if (data.length === 0) return true;
    // If all rows are empty or unsaved (isNew without meaningful data), disable export
    return data.every((row) => isRowEmpty(row) || (row.isNew && !row.saved));
  }, [data, exportInProgress, Allisbns?.length]);
  const handleExportButton = (e: React.MouseEvent<HTMLButtonElement>) => {
    e.preventDefault();
   
    downloadSegDetailsData({
      segId,
      org: selectedOrganization,
      DISTRIBUTION_CHANNEL_NAME: selectedDistribution,
      account: selectedAccountName,
    });
  };

  const getScalarValue = (value: any): string => {
    if (value === null || value === undefined || value === "") return "-";
    if (typeof value === "object") {
      if ("$numberDecimal" in value) return value.$numberDecimal || "-";
      return JSON.stringify(value);
    }
    return String(value);
  };

  const toLabel = (key: string): string =>
    key
      .replace(/([A-Z])/g, " $1")
      .replace(/_/g, " ")
      .replace(/\s+/g, " ")
      .trim()
      .replace(/^./, (c) => c.toUpperCase());

  const toNumber = (value: any): number => {
    if (value === null || value === undefined || value === "") return 0;
    if (typeof value === "object" && value.$numberDecimal !== undefined) {
      return parseFloat(value.$numberDecimal || "0") || 0;
    }
    return parseFloat(String(value)) || 0;
  };

  const ensurePageSpace = (
    doc: jsPDF,
    yState: { y: number },
    requiredHeight: number,
    margin: number
  ) => {
    const pageHeight = doc.internal.pageSize.getHeight();
    if (yState.y + requiredHeight > pageHeight - margin) {
      doc.addPage();
      yState.y = margin;
    }
  };

  const drawTwoColumnSection = (
    doc: jsPDF,
    yState: { y: number },
    margin: number,
    leftTitle: string,
    rightTitle: string,
    leftRows: Array<[string, any]>,
    rightRows: Array<[string, any]>
  ) => {
    const pageWidth = doc.internal.pageSize.getWidth();
    const contentWidth = pageWidth - margin * 2;
    const gutter = 12;
    const colWidth = (contentWidth - gutter) / 2;
    const leftX = margin;
    const rightX = margin + colWidth + gutter;
    const rowHeight = 16;
    const titleHeight = 18;
    const maxRows = Math.max(leftRows.length, rightRows.length);
    const blockHeight = titleHeight + (maxRows + 1) * rowHeight + 8;

    ensurePageSpace(doc, yState, blockHeight, margin);

    doc.setFont("helvetica", "bold");
    doc.setFontSize(10);
    doc.rect(leftX, yState.y, colWidth, titleHeight);
    doc.text(leftTitle, leftX + 6, yState.y + 12);
    doc.rect(rightX, yState.y, colWidth, titleHeight);
    doc.text(rightTitle, rightX + 6, yState.y + 12);
    yState.y += titleHeight;

    const drawTable = (x: number, rows: Array<[string, any]>) => {
      const keyWidth = Math.floor(colWidth * 0.38);
      const valWidth = colWidth - keyWidth;
      rows.forEach(([key, val], idx) => {
        const y = yState.y + idx * rowHeight;
        doc.setFont("helvetica", "bold");
        doc.rect(x, y, keyWidth, rowHeight);
        doc.text(key, x + 4, y + 11);
        doc.setFont("helvetica", "normal");
        doc.rect(x + keyWidth, y, valWidth, rowHeight);
        const valueText = getScalarValue(val);
        const clipped = doc.splitTextToSize(valueText, valWidth - 6)[0] || "-";
        doc.text(clipped, x + keyWidth + 3, y + 11);
      });
    };

    drawTable(leftX, leftRows);
    drawTable(rightX, rightRows);
    yState.y += maxRows * rowHeight + 8;
  };

  const drawOtisTable = (
    doc: jsPDF,
    yState: { y: number },
    margin: number,
    rows: Array<Record<string, string>>
  ) => {
    yState.y += 6;
    const pageWidth = doc.internal.pageSize.getWidth();
    const contentWidth = pageWidth - margin * 2;
    const rowHeight = 16;
    const cols = [
      "Distribution Channel/Name",
      "Organization Name/ID",
      "Account Name/ID",
      "INITIAL ORDERS",
      "LTD Orders",
      "Sales Notes",
      "Laydown Goal",
      "Laydown Estimate",
      "6-month Estimate (incl Laydown)",
      "COMP_TITLE INITIAL ORDER",
      "COMP_TITLE GROSS SALES",
      "COMP_TITLE NET SALES",
      "COMP_TITLE % RETURNS (TBD)",
      "COMP_TITLE 4wk POS (TBD)",
      "COMP_TITLE 8wk POS (TBD)",
      "COMP_TITLE LTD POS (TBD)",
    ];

    const widths = [70, 72, 72, 38, 42, 64, 42, 45, 50, 45, 45, 45, 42, 40, 40, 40];
    const widthTotal = widths.reduce((a, b) => a + b, 0);
    const scale = contentWidth / widthTotal;
    const scaled = widths.map((w) => w * scale);

    ensurePageSpace(doc, yState, rowHeight * 3, margin);
    doc.setFont("helvetica", "bold");
    doc.setFontSize(10);
    doc.text("OTIS Table", margin, yState.y);
    yState.y += 8;

    let xCursor = margin;
    doc.setFontSize(6.5);
    cols.forEach((col, idx) => {
      const w = scaled[idx];
      doc.rect(xCursor, yState.y, w, rowHeight);
      const txt = doc.splitTextToSize(col, w - 3)[0] || col;
      doc.text(txt, xCursor + 1.5, yState.y + 10.5);
      xCursor += w;
    });
    yState.y += rowHeight;

    doc.setFont("helvetica", "normal");
    rows.forEach((row) => {
      ensurePageSpace(doc, yState, rowHeight, margin);
      xCursor = margin;
      cols.forEach((col, idx) => {
        const w = scaled[idx];
        doc.rect(xCursor, yState.y, w, rowHeight);
        const value = row[col] ?? "-";
        const txt = doc.splitTextToSize(String(value), w - 3)[0] || "-";
        doc.text(txt, xCursor + 1.5, yState.y + 10.5);
        xCursor += w;
      });
      yState.y += rowHeight;
    });
    yState.y += 8;
  };

  const buildFlatOtisRows = (records: any[]): Array<Record<string, string>> => {
    const rows: Array<Record<string, string>> = [];
    records.forEach((record) => {
      const organizations = (record?.organizations || []).filter((org: any) =>
        (org?.accounts || []).some((acc: any) => acc?.accountName)
      );
      const allAccounts = organizations.flatMap((org: any) => org.accounts || []);
      const totalUnits = allAccounts.reduce(
        (sum: number, acc: any) => sum + (parseInt(String(acc?.units || 0), 10) || 0),
        0
      );

      const totalLtdUnits = allAccounts.reduce(
        (sum: number, acc: any) => sum + (parseInt(String(acc?.ltdUnits || 0), 10) || 0),
        0
      );

      const totalRow: Record<string, string> = {
        "Distribution Channel/Name":
          getScalarValue(record?.DISTRIBUTION_CHANNEL_NAME) !== "-"
            ? getScalarValue(record?.DISTRIBUTION_CHANNEL_NAME)
            : getScalarValue(record?.DISTRIBUTION_CHANNEL),
        "Organization Name/ID": "Total Organizations",
        "Account Name/ID": "Total Accounts",
        "INITIAL ORDERS": String(totalUnits),
        "LTD Orders": String(totalLtdUnits),
        "Sales Notes": "",
        "Laydown Goal": String(allAccounts.reduce((s: number, a: any) => s + toNumber(a?.pub_goal), 0)),
        "Laydown Estimate": String(allAccounts.reduce((s: number, a: any) => s + toNumber(a?.currEst), 0)),
        "6-month Estimate (incl Laydown)": String(
          allAccounts.reduce((s: number, a: any) => s + toNumber(a?.suppEst), 0)
        ),
        "COMP_TITLE INITIAL ORDER": String(allAccounts.reduce((s: number, a: any) => s + toNumber(a?.compInit), 0)),
        "COMP_TITLE GROSS SALES": String(allAccounts.reduce((s: number, a: any) => s + toNumber(a?.compGross), 0)),
        "COMP_TITLE NET SALES": String(allAccounts.reduce((s: number, a: any) => s + toNumber(a?.compNet), 0)),
        "COMP_TITLE % RETURNS (TBD)": String(
          allAccounts.reduce((s: number, a: any) => s + toNumber(a?.retPercentage), 0)
        ),
        "COMP_TITLE 4wk POS (TBD)": String(allAccounts.reduce((s: number, a: any) => s + toNumber(a?.cpos4wkPOS), 0)),
        "COMP_TITLE 8wk POS (TBD)": String(allAccounts.reduce((s: number, a: any) => s + toNumber(a?.cpos8wkPOS), 0)),
        "COMP_TITLE LTD POS (TBD)": String(allAccounts.reduce((s: number, a: any) => s + toNumber(a?.Cpos_LTD), 0)),
      };
      rows.push(totalRow);

      organizations.forEach((org: any) => {
        const validAccounts = (org?.accounts || []).filter((acc: any) => acc?.accountName);
        const orgUnits = validAccounts.reduce(
          (sum: number, a: any) => sum + (parseInt(String(a?.units || 0), 10) || 0),
          0
        );
        const orgLtdUnits = validAccounts.reduce(
          (sum: number, a: any) => sum + (parseInt(String(a?.ltdUnits || 0), 10) || 0),
          0
        );

        rows.push({
          "Distribution Channel/Name": "",
          "Organization Name/ID": `${getScalarValue(org?.orgName)} [${getScalarValue(org?.organizationId)}]`,
          "Account Name/ID": "All Accounts",
          "INITIAL ORDERS": String(orgUnits),
          "LTD Orders": String(orgLtdUnits),
          "Sales Notes": "",
          "Laydown Goal": String(validAccounts.reduce((s: number, a: any) => s + toNumber(a?.pub_goal), 0)),
          "Laydown Estimate": String(validAccounts.reduce((s: number, a: any) => s + toNumber(a?.currEst), 0)),
          "6-month Estimate (incl Laydown)": String(
            validAccounts.reduce((s: number, a: any) => s + toNumber(a?.suppEst), 0)
          ),
          "COMP_TITLE INITIAL ORDER": String(validAccounts.reduce((s: number, a: any) => s + toNumber(a?.compInit), 0)),
          "COMP_TITLE GROSS SALES": String(validAccounts.reduce((s: number, a: any) => s + toNumber(a?.compGross), 0)),
          "COMP_TITLE NET SALES": String(validAccounts.reduce((s: number, a: any) => s + toNumber(a?.compNet), 0)),
          "COMP_TITLE % RETURNS (TBD)": String(
            validAccounts.reduce((s: number, a: any) => s + toNumber(a?.retPercentage), 0)
          ),
          "COMP_TITLE 4wk POS (TBD)": String(validAccounts.reduce((s: number, a: any) => s + toNumber(a?.cpos4wkPOS), 0)),
          "COMP_TITLE 8wk POS (TBD)": String(validAccounts.reduce((s: number, a: any) => s + toNumber(a?.cpos8wkPOS), 0)),
          "COMP_TITLE LTD POS (TBD)": String(validAccounts.reduce((s: number, a: any) => s + toNumber(a?.Cpos_LTD), 0)),
        });

        const distRaw = (
          record?.DISTRIBUTION_CHANNEL ??
          record?.DISTRIBUTION_CHANNEL_NAME ??
          ""
        )
          .toString()
          .trim()
          .toUpperCase();
        const allowAccountRows = distRaw === "MASSMER" || distRaw === "RETINDEP";
        if (allowAccountRows) {
          validAccounts.forEach((account: any) => {
            rows.push({
              "Distribution Channel/Name": "",
              "Organization Name/ID": "",
              "Account Name/ID": `${getScalarValue(account?.accountName)} [${getScalarValue(
                account?.accountNumber
              )}]`,
              "INITIAL ORDERS": getScalarValue(account?.units),
              "LTD Orders": getScalarValue(account?.ltdUnits),
              "Sales Notes": getScalarValue(account?.sales_notes),
              "Laydown Goal": getScalarValue(account?.pub_goal),
              "Laydown Estimate": getScalarValue(account?.currEst),
              "6-month Estimate (incl Laydown)": getScalarValue(account?.suppEst),
              "COMP_TITLE INITIAL ORDER": getScalarValue(account?.compInit),
              "COMP_TITLE GROSS SALES": getScalarValue(account?.compGross),
              "COMP_TITLE NET SALES": getScalarValue(account?.compNet),
              "COMP_TITLE % RETURNS (TBD)": getScalarValue(account?.retPercentage),
              "COMP_TITLE 4wk POS (TBD)": getScalarValue(account?.cpos4wkPOS),
              "COMP_TITLE 8wk POS (TBD)": getScalarValue(account?.cpos8wkPOS),
              "COMP_TITLE LTD POS (TBD)": getScalarValue(account?.Cpos_LTD),
            });
          });
        }
      });
    });
    return rows;
  };

 

  const handleUploadClick = () => {
    fileInputRef.current?.click();
  };

  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const name = file.name.toLowerCase();
    if (!name.endsWith(".xlsx") && !name.endsWith(".xls") && !name.endsWith(".csv")) {
      toast.error("Please choose an Excel or CSV file (.xlsx, .xls, .csv).");
      e.target.value = "";
      return;
    }
    setLoadingTest(true);
    setUploading(true);
    try {
      const formData = new FormData();
      formData.append("file", file);
      const response = await axios.post(API.UPLOAD_OTIS_DATA, formData, {
        headers: { "Content-Type": "multipart/form-data" },
        // Very large OTIS uploads can run well beyond 5 minutes. Do not auto-timeout on client.
        timeout: 0,
      });
      if (response.data?.success) {
        if (response.data?.partial) {
          toast.success(response.data.message || "Upload partially complete.");
          if (response.data?.errors?.length) {
            console.warn("Upload partial errors:", response.data.errors);
          }
        } else {
          toast.success(response.data.message || "Upload complete.");
        }
        onRefetch?.();
      } else {
        const msg = response.data?.message || "We couldn't complete the upload. Please check your file and try again.";
        toast.error(msg);
        if (response.data?.errors?.length) console.error("Upload errors:", response.data.errors);
      }
    } catch (err: any) {
      const res = err.response?.data;
      const isTimeout = err?.code === "ECONNABORTED";
      const isCanceled =
        axios.isCancel?.(err) ||
        err?.name === "CanceledError" ||
        err?.message?.toLowerCase?.().includes("canceled");
      const msg =
        res?.message ||
        (isTimeout
          ? "Upload took too long and was timed out. Please try again."
          : isCanceled
            ? "Upload was canceled before completion. Please keep this page open and try again."
            : err.response
              ? "We couldn't complete the upload. Please check your file and try again."
              : "We couldn't reach the server. Please check your internet connection and try again.");
      toast.error(msg);
      if (res?.errors?.length) console.error("Upload errors:", res.errors);
    } finally {
      setLoadingTest(false);
      setUploading(false);
      e.target.value = "";
    }
  };

  const showActionButtons =
    (userRole?.includes("editor") || userRole?.includes("admin")) &&
    handleAddRow != null &&
    handleRemoveRowAction != null &&
    handleSaveAll != null &&
    setModal != null;
  const btnClass =
    "font-poppins text-white rounded-md font-semibold border border-none h-7 px-2 text-xs min-w-0 shrink-0";
  const btnDisabled = "bg-[#f87171] cursor-not-allowed";
  const btnEnabled = "bg-[#e31c23] cursor-pointer hover:bg-red-700";

  return (
    <>
      {isLoading && (
        <div className="mb-1 mt-2 flex items-center gap-2 rounded-md border border-red-100 bg-red-50 px-2 py-1 text-xs text-red-700">
          <span className="h-3 w-3 animate-spin rounded-full border-2 border-red-300 border-t-red-700" />
          Updating filters...
        </div>
      )}
      <div className="goto_p flex flex-nowrap items-center gap-x-2 gap-y-1 mb-1 mt-2">
        <div className="flex items-center gap-1 min-w-0 shrink-0">
          <ControlledSelect
            name="AboveRepCode"
            control={control}
            placeholder="Select Distribution Channel Name"
            options={distributionOptions}
            onChange={(value: OptionType | null) => handleFilterChange("AboveRepCode", value)}
            className="min-w-[121px] max-w-[146px] w-full shrink-0 text-xs"
          />
          <ControlledSelect
            name="AboveOrganization"
            control={control}
            placeholder="Select organizations"
            options={orgOption}
            onChange={(value: OptionType | null) => handleFilterChange("AboveOrganization", value)}
            className="min-w-[121px] max-w-[146px] w-full shrink-0 text-xs"
          />
          <ControlledSelect
            name="AboveAccountName"
            control={control}
            placeholder="Select account name"
            options={accountNameOptions}
            onChange={(value: OptionType | null) => handleFilterChange("AboveAccountName", value)}
            className="min-w-[121px] max-w-[146px] w-full shrink-0 text-xs"
          />
        </div>
        <div className="flex items-center gap-1 shrink-0">
          {!isViewer && (
            <>
              <input
                ref={fileInputRef}
                type="file"
                accept=".xlsx,.xls,.csv"
                className="hidden"
                onChange={handleFileChange}
              />
              <button
                type="button"
                disabled={uploading}
                onClick={handleUploadClick}
                className={`${btnClass} ${uploading ? btnDisabled : btnEnabled}`}
              >
                {uploading ? "Uploading…" : "Upload"}
              </button>
            </>
          )}
          <button
            disabled={isClearDisabled}
            onClick={handleClearSearchBoxes}
            className={`${btnClass} ${isClearDisabled ? btnDisabled : btnEnabled}`}
          >
            Clear Filters
          </button>
          <button
            disabled={isExportDisabled}
            onClick={handleExportButton}
            className={`${btnClass} ${isExportDisabled ? btnDisabled : btnEnabled}`}
          >
            {exportInProgress ? "Exporting…" : "Export"}
          </button>
          
          {showActionButtons && (
            <>
              <button
                disabled={addRow || isLoadingProp}
                onClick={handleAddRow}
                className={`${btnClass} ${isLoadingProp || addRow ? btnDisabled : btnEnabled}`}
              >
                Add Row
              </button>
              <button
                disabled={!addRow || isLoadingProp}
                onClick={() => handleRemoveRowAction("someId")}
                className={`${btnClass} ${isLoadingProp || !addRow ? btnDisabled : btnEnabled}`}
              >
                Cancel
              </button>
              <button
                onClick={handleSaveAll}
                disabled={isSaving || isLoadingProp}
                className={`${btnClass} ${isSaving || isLoadingProp ? btnDisabled : btnEnabled}`}
              >
                Save All
              </button>
              {/*
              <button
                onClick={() => setModal(true)}
                disabled={!!isLoadingProp}
                className={`${btnClass} ${isLoadingProp ? btnDisabled : btnEnabled}`}
              >
                Clear All
              </button>
              */}
            </>
          )}
        </div>
      </div>
    </>
  );
};

export default SearchControls;